import { useMemo, Dispatch, SetStateAction } from 'react'
import { Button } from 'primereact/button'
import { Tooltip } from 'primereact/tooltip'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// local imports
import { IFilteredDoctorList } from 'types/HealthType'
import DateUtil from 'utils/DateUtil'
import DatePickerIcon from 'components/ui/DatePickerIcon'
import DoctorPhoto from '../doctor/DoctorPhoto'
import { DocLocation, DocSpeciality } from '../doctor/DoctorCard'

interface ModalHeaderProps {
  availableDateRange: { start: number; end: number }
  filterDate: Date
  setFilterDate: Dispatch<SetStateAction<Date>>
  setSelectedDoc: Dispatch<SetStateAction<IFilteredDoctorList | undefined>>
  doctor: IFilteredDoctorList | undefined
  doctors: IFilteredDoctorList[] | []
  setTableWeek: Dispatch<SetStateAction<{ start: Date; end: Date }>>
  selectedDateStr?: string
}

function containsOnlySpaces(str: string | undefined) {
  if (!str) return
  return str.trim().length === 0
}

export default function AppointmentModalHeader({
  availableDateRange: availableDates,
  selectedDateStr,
  filterDate,
  setFilterDate,
  setTableWeek,
  doctor,
  doctors,
  setSelectedDoc,
}: ModalHeaderProps) {
  const { t } = useTranslation()
  const { id, employeeId, title, name, surname, specialty, assistantFullName, explanation, location, block, floor } =
    doctor || {}

  // Variables
  const length = doctors?.length
  const btnDisabled = length <= 1
  const currentDocIndex = useMemo(
    () => doctors.findIndex((docs) => docs.healthServiceDoctorId === doctor?.healthServiceDoctorId),
    [doctors, doctor?.healthServiceDoctorId]
  )

  /* Doctor Slider */
  const nextSlide = () =>
    currentDocIndex === length - 1 ? setSelectedDoc(doctors[0]) : setSelectedDoc(doctors[currentDocIndex + 1])
  const prevSlide = () =>
    currentDocIndex === 0 ? setSelectedDoc(doctors[length - 1]) : setSelectedDoc(doctors[currentDocIndex - 1])

  /* Handle Appointment Date change input */
  const handleDateChange = (e: any) => {
    if (e.value) {
      const { start, end } = DateUtil.getWeekStartEndFromDate(e.value)
      setTableWeek({ start, end })
      setFilterDate(e.value)
    }
  }

  const assistantName = containsOnlySpaces(assistantFullName)
    ? t('appointment-modal.assistant-not-found')
    : assistantFullName

  return (
    <DoctorHeaderInfo>
      <div className="doctor-info">
        <div className="slider-left-arrow">
          <Button
            id="doctorPrev"
            disabled={btnDisabled}
            onClick={prevSlide}
            icon="pi pi-chevron-left"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>

        <div className="doc-photo">
          <DoctorPhoto doctorId={id || 0} employeeId={employeeId} IsAbsolute={false} size={[54, 54]} />
        </div>

        <div className="doctor-summary">
          <span className="doc-name-surname">
            {title} {`${name} ${surname}`}
          </span>

          <DocSpeciality style={{ display: 'flex', alignItems: 'center', columnGap: '0.5rem' }}>
            <span>{specialty || '---'}</span>
            {!!explanation ? (
              <>
                <Tooltip
                  style={{ maxWidth: '600px' }}
                  target=".explanation-tooltip"
                  position="right"
                  content={explanation}
                />
                <i className="pi pi-info-circle explanation-tooltip"></i>
              </>
            ) : null}
          </DocSpeciality>

          <span className="doc-info-text">
            <span className="fw-bold">{t('appointment-modal.doctor-assistant')} : </span> {assistantName}
          </span>
          <span className="doc-info-text">{location}</span>
          <DocLocation style={{ fontWeight: 500 }}>
            {block} | {floor}
          </DocLocation>
        </div>

        <div className="slider-right-arrow">
          <Button
            id="doctorNext"
            disabled={btnDisabled}
            onClick={nextSlide}
            icon="pi pi-chevron-right"
            className="p-button-plain p-button-rounded p-button-text"
          />
        </div>
      </div>

      <div className="d-flex flex-column align-items-center justify-content-end ">
        {!!selectedDateStr ? (
          <div className="calendar-notify">{t('calendar.selected-day-info-text', { date: selectedDateStr })}</div>
        ) : null}
      </div>

      <div className="appointment-date">
        <DatePickerIcon
          idName="doctorModalReservationDate"
          showOtherMonths
          selectOtherMonths
          showOnFocus
          appendTo={document.body}
          disabledDays={[]}
          defaultDate={filterDate}
          minDate={DateUtil.getPastDate(availableDates.start)}
          maxDate={DateUtil.getFutureDate(availableDates.end)}
          label={t('input-texts.appointment-date')}
          onSelectedDateChange={handleDateChange}
          onFocus={() => {}}
        />
      </div>
    </DoctorHeaderInfo>
  )
}

const DoctorHeaderInfo = styled.div`
  margin: 0.5rem 0 0.5rem 0;
  display: flex;
  justify-content: space-between;
  // align-items: flex-start;

  .doctor-info {
    position: relative;
    max-width: 400px;
    display: flex;
    align-items: flex-start;

    .slider-left-arrow {
      position: absolute;
      top: 0;
      left: 0;
    }

    .slider-right-arrow {
      position: absolute;
      top: 0;
      right: 0;
    }

    .doc-photo {
      width: 55px;
      height: 55px;
      margin: 0 12px 0 50px;
    }

    .doctor-summary {
      flex-grow: 1;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 2px;
      margin-right: 40px;

      .doc-info-text {
        font-size: 12px;
        font-weight: 500;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 3;
        -webkit-box-orient: vertical;
      }

      .doc-name-surname {
        font-size: 14px;
        font-weight: 700;
        margin-right: 12px;
        text-overflow: ellipsis;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
      }
    }
  }

  .doctor-explanation {
    // flex-grow: 1;
    font-size: 12px;
    max-width: 720px;
    text-overflow: ellipsis;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
  }

  .calendar-notify {
    margin: 1rem 0;
    font-size: 14px;
    color: #222e47;
    line-height: 17px;
    font-weight: 700;
  }

  .appointment-date {
    min-width: 265px;
  }
`
